'use client';
import { useEffect, useState } from 'react';

import cn from '@/lib/clsxm';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import { VARIANT_ENUM } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import Props from '@/components/app/product/components/action-buttons/type';
import Actions from '@/components/shared/components/cards/@shared/actions';

import { useAddonsStore } from '@/store/addons.store';
import { useTourStore } from '@/store/tour.store';

export default function ActionButtons({
  slug,
  className,
  productId,
  hasPreviewButton = true,
  isFree,
  isSubscription,
  subscriptionDefaultPriceIsOneYear,
  dataCy,
  ...props
}: Props) {
  const { showTour } = useTourStore();
  const [isSticky, setIsSticky] = useState(false);
  const isSmallScreen = useClientSmallScreen();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        if (window.scrollY > window.innerHeight - 45) {
          if (!isSticky) {
            setIsSticky(true);
          }
        } else {
          if (isSticky) {
            setIsSticky(false);
          }
        }
      };

      if (isSmallScreen) {
        window.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isSmallScreen, isSticky]);

  // Keep Goftino's launcher lifted above this bar's reserved space for the whole product page
  // visit, not just while `isSticky` is actually true — the bar's own `fixed bottom-0` state
  // toggles on scroll, and tying the offset to that made the icon visibly jump up/down with it,
  // which wasn't wanted. A fixed offset for the page's lifetime avoids that motion entirely.
  // Applied via a CSS class (see `.goftino-above-sticky-cta` in styles/index.css) rather than
  // Goftino's own setWidget({ marginBottom }): that option only takes effect if set before the
  // widget's first render (its client.js reads the config once at init, not reactively).
  useEffect(() => {
    if (!isSmallScreen) {
      return;
    }
    // On <html>, not <body>: Goftino's own script controls where it injects its container, and
    // <html> is the one ancestor every node in the document is guaranteed to be inside, so the
    // CSS selector below can't miss it regardless of where that container actually lives.
    document.documentElement.classList.add('goftino-above-sticky-cta');
    return () => {
      document.documentElement.classList.remove('goftino-above-sticky-cta');
    };
  }, [isSmallScreen]);

  return (
    <Container data-cy={dataCy} className={cn('relative', className)}>
      <Container
        center
        className={cn(
          'z-30 -mt-4 w-full translate-y-0 transform gap-2.5 px-4 transition-all duration-300 md:mt-0 md:gap-[15px] md:p-6',
          {
            'fixed bottom-0 transform bg-[#FFFFFF] p-4': isSticky,
            relative: !isSticky || showTour,
          },
          !hasPreviewButton && 'justify-end',
        )}
      >
        <Actions
          isFree={isFree}
          className='h-[55px] md:max-w-[50%]'
          hasIcon
          slug={slug}
          productId={productId}
          hasPreviewButton={hasPreviewButton}
          previewButtonVariant={VARIANT_ENUM.SOUL}
          isSubscription={isSubscription}
          subscriptionDefaultPriceIsOneYear={subscriptionDefaultPriceIsOneYear}
        />
      </Container>
    </Container>
  );
}
